import { Input, InputProps } from 'antd';
import { useCallback } from 'react';
import { useInputController } from '../hooks/useInputController';
import { useFormItemAccessibility } from '../item/useFormItemAccessibility';
import { useFormItemEnabled } from '../item/useFormItemEnabled';

export interface INumberInputProps extends Omit<InputProps, 'name' | 'type'> {
  name: string;
  onAfterChange?: (value: number | null) => void;
  format?: (value: number | string | null) => string | number;
  parse?: (value: number | string | null) => number;
}

export function NumberInput(props: INumberInputProps) {
  const {
    name,
    onAfterChange,
    format = value => value ?? '',
    parse = value => Number(value),
    ...inputProps
  } = props;

  const { enabled, layoutEnabled } = useFormItemEnabled();

  const {
    field: { value, onChange, onBlur, ref },
  } = useInputController(name, { enabled });

  const accessibility = useFormItemAccessibility();

  // Forzo il salvataggio del dato il dato come numero
  const handleChange = useCallback(
    (event: any) => {
      if (event.target.value === '') {
        onChange(null);
        onAfterChange?.(null);
        return;
      }
      const parsedValue = parse(Number(event.target.value));

      // Il minimo e il massimo creano dei bug in fase di scrittura. Da rivedere.
      if (inputProps.max != null && parsedValue > Number(inputProps.max)) {
        onChange(inputProps.max);
        onAfterChange?.(Number(inputProps.max));
        return;
      }
      if (inputProps.min != null && parsedValue < Number(inputProps.min)) {
        onChange(inputProps.min);
        onAfterChange?.(Number(inputProps.min));
        return;
      }
      onChange(parsedValue);
      onAfterChange?.(parsedValue);
    },
    [inputProps.max, inputProps.min, onAfterChange, onChange],
  );

  return (
    <Input
      size="large"
      {...accessibility.attributes}
      ref={ref}
      disabled={enabled === false || layoutEnabled === false}
      {...inputProps}
      value={format(inputProps.value ?? value)}
      onChange={handleChange}
      onBlur={onBlur}
      name={name}
      suffix={props.suffix ?? <span aria-hidden="true" />} // See https://ant.design/components/input#why-input-lose-focus-when-change-prefixsuffixshowcount
      type="number"
    />
  );
}
